/**
 * Lazy-loaded component definitions with performance optimizations
 * This file defines all components that should be lazy-loaded for better performance
 */

import React, { type ComponentType } from 'react';
import { createLazyComponent, lazyComponentImport } from '../utils/performance';
import { withLazyLoading } from '../utils/lazyUtils';
import { Box, Typography, CircularProgress } from '@mui/material';

// Loading fallback component
const LoadingFallback: React.FC<{ componentName: string }> = ({ componentName }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: 2,
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body1" color="text.secondary">
      Loading {componentName}...
    </Typography>
  </Box>
);

// Error fallback component  
const ErrorFallback: React.FC<{ componentName: string }> = ({ componentName }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: 2,
      p: 3,
    }}
  >
    <Typography variant="h6" color="error">
      Failed to load {componentName}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Please refresh the page or try again later.
    </Typography>
  </Box>
);

// Lazy-loaded advanced components
export const LazyOCRResultsReview = createLazyComponent(
  () => import('./advanced/OCRResultsReview'),
  () => <ErrorFallback componentName="OCR Results Review" />
);

export const LazyLocationTracking = createLazyComponent(
  () => import('./advanced/LocationTracking'),
  () => <ErrorFallback componentName="CCI Spatial" />
);

export const LazyTenantUtilization = createLazyComponent(
  () => import('../pages/ProviderSettings/tabs/TenantUtilization'),
  () => <ErrorFallback componentName="Tenant Utilization" />
);

export const LazyEFEMIntegration = createLazyComponent(
  lazyComponentImport(() => import('./advanced/EFEMIntegration')),
  () => <ErrorFallback componentName="Axon Integration" />
);

export const LazyWaferSlotUpdate = createLazyComponent(
  () => import('./operations/WaferSlotUpdate'),
  () => <ErrorFallback componentName="Wafer Slot Update" />
);

export const LazyTempFilesPage = createLazyComponent(
  () => import('../pages/TempFilesPage'),
  () => <ErrorFallback componentName="Temp files" />
);

// Lazy-loaded core components
export const LazyInventoryDashboard = createLazyComponent(
  () => import('./common/DashboardOverview'),
  () => <ErrorFallback componentName="Inventory Dashboard" />
);

export const LazyConflictManagement = createLazyComponent(
  () => import('../pages/Operations/ConflictResolutionPage'),
  () => <ErrorFallback componentName="Conflict Resolution" />
);

// Lazy-loaded operational components
export const LazyUserManagement = createLazyComponent(
  () => import('./RouteWrappers').then((module) => ({ default: module.UserManagementWrapper as ComponentType })),
  () => <ErrorFallback componentName="User Management" />
);

export const LazyCustomerManagement = createLazyComponent(
  () => import('./customer/CustomerManagementContainer'),
  () => <ErrorFallback componentName="Customer Management" />
);

export const LazyContactManagement = createLazyComponent(
  () => import('./contact/ContactManagementContainer'),
  () => <ErrorFallback componentName="Contact Management" />
);

export const LazyGroupManagement = createLazyComponent(
  lazyComponentImport(() => import('./admin/GroupManagementContainer')),
  () => <ErrorFallback componentName="Group Management" />
);

export const LazyContainerTracking = createLazyComponent(
  () => import('./FunctionalWrappers').then((module) => ({ default: module.ContainerManagementFunctional as ComponentType })),
  () => <ErrorFallback componentName="Container Tracking" />
);


// Additional wrapper components
export const LazyWaferManagement = createLazyComponent(
  () => import('./FunctionalWrappers').then((module) => ({ default: module.WaferManagementFunctional as ComponentType })),
  () => <ErrorFallback componentName="Wafer Management" />
);

export const LazyHistoryTracking = createLazyComponent(
  () => import('./RouteWrappers').then((module) => ({ default: module.HistoryTrackingWrapper as ComponentType })),
  () => <ErrorFallback componentName="History Tracking" />
);

export const LazyLocationTrackingWrapper = createLazyComponent(
  () => import('./advanced/LocationTracking'),
  () => <ErrorFallback componentName="CCI Spatial" />
);

export const LazyTagCommandCenter = createLazyComponent(
  () => import('./location/TagCommandCenter'),
  () => <ErrorFallback componentName="Node Command Center" />
);

export const LazyFloorPlanAdmin = createLazyComponent(
  () => import('./location/FloorPlanAdmin'),
  () => <ErrorFallback componentName="Cleanroom Layout" />
);

export const LazyProviderLocationIntegrationAdmin = createLazyComponent(
  () => import('./location/ProviderLocationIntegrationAdmin'),
  () => <ErrorFallback componentName="Location Integration Admin" />
);

export const LazyProviderWaferIdModelTrainingPage = createLazyComponent(
  () => import('./vision/ProviderWaferIdModelTrainingPage'),
  () => <ErrorFallback componentName="CCI Vision Model Training" />
);

export const LazyProviderSiteAccessAdmin = createLazyComponent(
  () => import('./location/ProviderSiteAccessAdmin'),
  () => <ErrorFallback componentName="Site Access Admin" />
);

export const LazyLocationOverviewPage = createLazyComponent(
  () => import('./location/LocationOverviewPage'),
  () => <ErrorFallback componentName="Location Overview" />
);

export const LazyTagFleetHealthPage = createLazyComponent(
  () => import('./location/TagFleetHealthPage'),
  () => <ErrorFallback componentName="Node Fleet Health" />
);

export const LazySiteAnchorHealthPage = createLazyComponent(
  () => import('./location/SiteAnchorHealthPage'),
  () => <ErrorFallback componentName="Satellite Health" />
);

export const LazyLocationGatewayConfigPage = createLazyComponent(
  () => import('./location/LocationGatewayConfigPage'),
  () => <ErrorFallback componentName="Matrix Config" />
);

export const LazyUwbDeploymentPage = createLazyComponent(
  () => import('./location/UwbDeploymentPage'),
  () => <ErrorFallback componentName="Matrix Deployment" />
);

export const LazyProviderOperationsDashboard = createLazyComponent(
  () => import('./location/ProviderOperationsDashboard'),
  () => <ErrorFallback componentName="Operations Dashboard" />
);

export const LazyUwbDeploymentStatusDashboard = createLazyComponent(
  () => import('./location/UwbDeploymentStatusDashboard'),
  () => <ErrorFallback componentName="Matrix Status" />
);

export const LazyProviderSupportViewer = createLazyComponent(
  () => import('./location/ProviderSupportViewer'),
  () => <ErrorFallback componentName="Support Viewer" />
);

// Pre-wrapped components ready for use
export const OCRResultsReview = withLazyLoading(
  LazyOCRResultsReview,
  () => <LoadingFallback componentName="OCR Results Review" />
);

export const LocationTracking = withLazyLoading(
  LazyLocationTracking,
  () => <LoadingFallback componentName="CCI Spatial" />
);

export const TenantUtilization = withLazyLoading(
  LazyTenantUtilization,
  () => <LoadingFallback componentName="Tenant Utilization" />
);

export const EFEMIntegration = withLazyLoading(
  LazyEFEMIntegration,
  () => <LoadingFallback componentName="Axon Integration" />
);

export const WaferSlotUpdate = withLazyLoading(
  LazyWaferSlotUpdate,
  () => <LoadingFallback componentName="Wafer Slot Update" />
);

export const TempFilesPage = withLazyLoading(
  LazyTempFilesPage,
  () => <LoadingFallback componentName="Temp files" />
);

export const InventoryDashboard = withLazyLoading(
  LazyInventoryDashboard,
  () => <LoadingFallback componentName="Inventory Dashboard" />
);

export const ConflictManagement = withLazyLoading(
  LazyConflictManagement,
  () => <LoadingFallback componentName="Conflict Management" />
);

export const UserManagement = withLazyLoading(
  LazyUserManagement,
  () => <LoadingFallback componentName="User Management" />
);

export const CustomerManagement = withLazyLoading(
  LazyCustomerManagement,
  () => <LoadingFallback componentName="Customer Management" />
);

export const ContactManagement = withLazyLoading(
  LazyContactManagement,
  () => <LoadingFallback componentName="Contact Management" />
);

export const GroupManagementContainer = withLazyLoading(
  LazyGroupManagement,
  () => <LoadingFallback componentName="Group Management" />
);

export const ContainerTracking = withLazyLoading(
  LazyContainerTracking,
  () => <LoadingFallback componentName="Container Tracking" />
);

export const WaferManagement = withLazyLoading(
  LazyWaferManagement,
  () => <LoadingFallback componentName="Wafer Management" />
);

export const HistoryTracking = withLazyLoading(
  LazyHistoryTracking,
  () => <LoadingFallback componentName="History Tracking" />
);

export const LocationTrackingComponent = withLazyLoading(
  LazyLocationTrackingWrapper,
  () => <LoadingFallback componentName="CCI Spatial" />
);

export const TagCommandCenter = withLazyLoading(
  LazyTagCommandCenter,
  () => <LoadingFallback componentName="Node Command Center" />
);

export const FloorPlanAdmin = withLazyLoading(
  LazyFloorPlanAdmin,
  () => <LoadingFallback componentName="Cleanroom Layout" />
);

export const ProviderLocationIntegrationAdmin = withLazyLoading(
  LazyProviderLocationIntegrationAdmin,
  () => <LoadingFallback componentName="Location Integration Admin" />
);

export const ProviderWaferIdModelTrainingPage = withLazyLoading(
  LazyProviderWaferIdModelTrainingPage,
  () => <LoadingFallback componentName="CCI Vision Model Training" />
);

export const ProviderSiteAccessAdmin = withLazyLoading(
  LazyProviderSiteAccessAdmin,
  () => <LoadingFallback componentName="Site Access Admin" />
);

export const LocationOverviewPage = withLazyLoading(
  LazyLocationOverviewPage,
  () => <LoadingFallback componentName="Location Overview" />
);

export const TagFleetHealthPage = withLazyLoading(
  LazyTagFleetHealthPage,
  () => <LoadingFallback componentName="Node Fleet Health" />
);

export const SiteAnchorHealthPage = withLazyLoading(
  LazySiteAnchorHealthPage,
  () => <LoadingFallback componentName="Satellite Health" />
);

export const LocationGatewayConfigPage = withLazyLoading(
  LazyLocationGatewayConfigPage,
  () => <LoadingFallback componentName="Matrix Config" />
);

export const UwbDeploymentPage = withLazyLoading(
  LazyUwbDeploymentPage,
  () => <LoadingFallback componentName="Matrix Deployment" />
);

export const ProviderOperationsDashboard = withLazyLoading(
  LazyProviderOperationsDashboard,
  () => <LoadingFallback componentName="Operations Dashboard" />
);

export const UwbDeploymentStatusDashboard = withLazyLoading(
  LazyUwbDeploymentStatusDashboard,
  () => <LoadingFallback componentName="Matrix Status" />
);

export const ProviderSupportViewer = withLazyLoading(
  LazyProviderSupportViewer,
  () => <LoadingFallback componentName="Support Viewer" />
);

// Export individual components and utilities
// Note: Default export removed to comply with react-refresh/only-export-components

// ── Operations Job & Workflow pages ─────────────────────────────────────────

export const LazyOperationsJobsPage = createLazyComponent(
  () => import('../pages/Operations/OperationsJobsPage'),
  () => <ErrorFallback componentName="Operations Jobs" />,
);

export const LazyJobCreationWizardPage = createLazyComponent(
  () => import('../pages/Operations/JobCreationWizardPage'),
  () => <ErrorFallback componentName="Job Creation Wizard" />,
);

export const LazyContainerSlotScanJobReviewPage = createLazyComponent(
  lazyComponentImport(() => import('../pages/Operations/ContainerSlotScanJobReviewPage')),
  () => <ErrorFallback componentName="Slot Scan Review" />,
);

export const LazyOperationsDraftsPage = createLazyComponent(
  () => import('../pages/Operations/OperationsDraftsPage'),
  () => <ErrorFallback componentName="My Drafts" />,
);

export const LazyContainerCreateWorkflowPage = createLazyComponent(
  () => import('../pages/Operations/ContainerCreateWorkflowPage'),
  () => <ErrorFallback componentName="Create Container" />,
);

export const LazyWaferDataRefreshWorkflowPage = createLazyComponent(
  () => import('../pages/Operations/WaferDataRefreshWorkflowPage'),
  () => <ErrorFallback componentName="Wafer Data Refresh" />,
);

export const LazyWaferIdAcquirePage = createLazyComponent(
  () => import('../pages/Operations/WaferIdAcquirePage'),
  () => <ErrorFallback componentName="Wafer ID Acquire" />,
);

export const LazyAuditWorkflowPage = createLazyComponent(
  () => import('../pages/Operations/AuditWorkflowPage'),
  () => <ErrorFallback componentName="Audit Workflow" />,
);

export const OperationsJobsPage = withLazyLoading<{ initialJobId?: string }>(
  LazyOperationsJobsPage as React.LazyExoticComponent<React.ComponentType<{ initialJobId?: string }>>,
  () => <LoadingFallback componentName="Operations Jobs" />,
);

export const JobCreationWizardPage = withLazyLoading(
  LazyJobCreationWizardPage,
  () => <LoadingFallback componentName="Job Creation Wizard" />,
);

export const ContainerSlotScanJobReviewPage = withLazyLoading<{ jobId: string }>(
  LazyContainerSlotScanJobReviewPage as React.LazyExoticComponent<React.ComponentType<{ jobId: string }>>,
  () => <LoadingFallback componentName="Slot Scan Review" />,
);

export const OperationsDraftsPage = withLazyLoading(
  LazyOperationsDraftsPage,
  () => <LoadingFallback componentName="My Drafts" />,
);

export const ContainerCreateWorkflowPage = withLazyLoading(
  LazyContainerCreateWorkflowPage,
  () => <LoadingFallback componentName="Create Container" />,
);

export const WaferDataRefreshWorkflowPage = withLazyLoading(
  LazyWaferDataRefreshWorkflowPage,
  () => <LoadingFallback componentName="Wafer Data Refresh" />,
);

export const WaferIdAcquirePage = withLazyLoading(
  LazyWaferIdAcquirePage,
  () => <LoadingFallback componentName="Wafer ID Acquire" />,
);

export const AuditWorkflowPage = withLazyLoading(
  LazyAuditWorkflowPage,
  () => <LoadingFallback componentName="Audit Workflow" />,
);

export const LazyLabelLinkRouter = createLazyComponent(
  () => import('../pages/labels/LabelLinkRouter'),
  () => <ErrorFallback componentName="Link Label" />,
);

export const LazyLabelLinkMobileOnlyRoute = createLazyComponent(
  () => import('../pages/labels/LabelLinkMobileOnlyRoute'),
  () => <ErrorFallback componentName="Link Label" />,
);

export const LazyAuditRunsPage = createLazyComponent(
  () => import('../pages/Operations/AuditRunsPage'),
  () => <ErrorFallback componentName="Audit Runs" />,
);

export const AuditRunsPage = withLazyLoading(
  LazyAuditRunsPage,
  () => <LoadingFallback componentName="Audit Runs" />,
);

export const LabelLinkMobileOnlyRoute = withLazyLoading(
  LazyLabelLinkMobileOnlyRoute,
  () => <LoadingFallback componentName="Link Label" />,
);

export const LazyInsightFieldModeRouter = createLazyComponent(
  () => import('../pages/mobile/InsightFieldModeRouter'),
  () => <ErrorFallback componentName="Insight Field Mode" />,
);

export const InsightFieldModeRouter = withLazyLoading(
  LazyInsightFieldModeRouter,
  () => <LoadingFallback componentName="Insight Field Mode" />,
);

export const LazyMobileInventoryRedirect = createLazyComponent(
  lazyComponentImport(() => import('../pages/mobile/MobileInventoryRedirect')),
  () => <ErrorFallback componentName="Mobile redirect" />,
);